/**
 * Quick View Dialog
 *
 * Custom element that manages the Quick View drawer. Triggered by any
 * element with a [data-quick-view-url] attribute anywhere on the page.
 *
 * @typedef {Object} QuickViewDialogElements
 * @property {HTMLDialogElement} dialog - The native dialog element
 * @property {HTMLElement} content - The content injection target
 */

class QuickViewDialog extends HTMLElement {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {string} */
  #loadingMarkup = '';

  /** @type {string} */
  #currentProductUrl = '';

  /** @type {number} */
  #requestId = 0;

  /** @type {AbortController | null} */
  #activeFetchController = null;

  /** @type {number} */
  #savedScrollY = 0;

  /** @type {number} */
  #currentSlide = 0;

  /** @type {number} */
  #touchStartX = 0;

  /** @type {HTMLDialogElement | null} */
  dialog = null;

  /** @type {HTMLElement | null} */
  content = null;

  connectedCallback() {
    this.dialog = this.querySelector('[ref="dialog"]');
    this.content = this.querySelector('[ref="content"]');

    if (!this.dialog || !this.content) return;

    this.#loadingMarkup = this.content.innerHTML;

    // Delegate trigger clicks from anywhere on the page
    document.addEventListener('click', this.#onBodyClick);

    // Delegate close / gallery / qty clicks inside drawer
    this.addEventListener('click', this.#onDrawerClick);

    // Native dialog cancel event (Escape key)
    this.dialog.addEventListener('cancel', this.#onCancel);

    // Backdrop click — click directly on <dialog>, not its children
    this.dialog.addEventListener('click', this.#onBackdropClick);

    // Invalidate cached section HTML when the cart changes so the next open
    // re-fetches fresh allocation state (e.g. purchase → wishlist after ATC).
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onBodyClick);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    this.#activeFetchController?.abort();
  }

  #onCartUpdate = () => {
    this.#cache.clear();
  };

  // ── Event handlers ────────────────────────────────────────────────────────

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  /** @param {MouseEvent} event */
  #onBackdropClick = (event) => {
    if (event.target === this.dialog) {
      this.#close();
    }
  };

  /** @param {MouseEvent} event */
  #onBodyClick = async (event) => {
    const trigger = /** @type {Element} */ (event.target).closest('[data-quick-view-url]');
    if (!trigger) return;

    event.preventDefault();
    const url = /** @type {HTMLElement} */ (trigger).dataset.quickViewUrl;
    if (!url) return;

    await this.#open(url);
  };

  /** @param {MouseEvent} event */
  #onDrawerClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    // Close buttons
    if (target.closest('[data-qv-close]')) {
      this.#close();
      return;
    }

    // Gallery thumbnail
    const thumb = target.closest('[data-qv-thumb]');
    if (thumb) {
      const index = parseInt(/** @type {HTMLElement} */ (thumb).dataset.qvThumb ?? '0', 10);
      this.#showSlide(index);
      return;
    }

    // Gallery navigation
    if (target.closest('[data-qv-prev]')) {
      this.#navGallery(-1);
      return;
    }

    if (target.closest('[data-qv-next]')) {
      this.#navGallery(1);
      return;
    }

    // Qty controls
    if (target.closest('[data-qv-qty-dec]')) {
      this.#adjustQty(-1);
      return;
    }

    if (target.closest('[data-qv-qty-inc]')) {
      this.#adjustQty(1);
    }
  };

  // ── Open / close ──────────────────────────────────────────────────────────

  /**
   * Opens the drawer and loads product content.
   *
   * @param {string} productUrl - Product URL (relative)
   * @returns {Promise<void>}
   */
  async #open(productUrl) {
    if (!this.dialog || !this.content) return;

    this.#requestId += 1;
    const currentRequestId = this.#requestId;

    this.#activeFetchController?.abort();
    this.#activeFetchController = new AbortController();

    this.#currentProductUrl = productUrl;
    this.#savedScrollY = window.scrollY;
    this.dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    this.dialog.setAttribute('data-loading', '');
    this.content.innerHTML = this.#loadingMarkup;

    // Move initial focus to close button inside drawer
    const closeBtn = this.dialog.querySelector('[data-qv-close]');
    if (closeBtn instanceof HTMLElement) {
      closeBtn.focus();
    }

    try {
      const html = await this.#fetchContent(productUrl, this.#activeFetchController.signal);
      if (currentRequestId !== this.#requestId) return;

      this.content.innerHTML = html;
      this.#currentSlide = 0;
      this.#initGallery();
      this.#initForm();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (currentRequestId !== this.#requestId) return;

      this.content.innerHTML = '<p style="padding: 2rem; font-family: var(--font-body--family);">Could not load product details.</p>';
    } finally {
      if (currentRequestId !== this.#requestId) return;
      this.dialog.removeAttribute('data-loading');
    }
  }

  /** Closes the drawer with slide-out animation, then restores scroll. */
  #close() {
    if (!this.dialog) return;
    if (!this.dialog.open) return;

    this.dialog.setAttribute('data-closing', '');

    this.dialog.addEventListener('animationend', () => {
      if (!this.dialog) return;
      this.dialog.removeAttribute('data-closing');
      this.dialog.close();
      document.documentElement.removeAttribute('scroll-lock');
      window.scrollTo(0, this.#savedScrollY);
    }, { once: true });
  }

  // ── Section Rendering API fetch ───────────────────────────────────────────

  /**
   * Fetches Quick View HTML for a product, with in-memory caching.
   *
   * @param {string} productUrl
   * @param {AbortSignal} [signal]
   * @returns {Promise<string>}
   */
  async #fetchContent(productUrl, signal) {
    if (this.#cache.has(productUrl)) {
      return /** @type {string} */ (this.#cache.get(productUrl));
    }

    const url = new URL(productUrl, window.location.origin);

    // Product URLs can sometimes include an absolute storefront domain or
    // existing query params. Always request through current origin and append
    // section params safely so theme preview/session auth is preserved.
    const requestUrl = new URL(`${url.pathname}${url.search}`, window.location.origin);
    requestUrl.searchParams.set('sections', 'section-rendering-quick-view');

    const resp = await fetch(requestUrl.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    });
    if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);

    const json = await resp.json();
    const html = json['section-rendering-quick-view'];

    if (!html) throw new Error('Section HTML not found in response');

    this.#cache.set(productUrl, html);
    return html;
  }

  // ── Gallery ───────────────────────────────────────────────────────────────

  /** Initialises gallery state after content injection. */
  #initGallery() {
    this.#showSlide(0);
    this.#initTouch();
  }

  /** Attaches touch-swipe handlers for mobile gallery navigation. */
  #initTouch() {
    const slides = this.content?.querySelector('.qv-gallery__slides');
    if (!slides) return;
    slides.addEventListener('touchstart', /** @type {EventListener} */ (this.#onTouchStart), { passive: true });
    slides.addEventListener('touchend', /** @type {EventListener} */ (this.#onTouchEnd), { passive: true });
  }

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    const touch = event.changedTouches[0];
    if (touch) this.#touchStartX = touch.clientX;
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - this.#touchStartX;
    if (Math.abs(deltaX) > 30) {
      this.#navGallery(deltaX < 0 ? 1 : -1);
    }
  };

  /**
   * Shows a specific slide and updates thumbnail active states.
   *
   * @param {number} index
   */
  #showSlide(index) {
    if (!this.content) return;

    const slides = this.content.querySelectorAll('[data-qv-slide]');
    const thumbs = this.content.querySelectorAll('[data-qv-thumb]');

    if (!slides.length) return;

    this.#currentSlide = Math.max(0, Math.min(index, slides.length - 1));

    for (const [i, slide] of slides.entries()) {
      const isActive = i === this.#currentSlide;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    }

    for (const [i, thumb] of thumbs.entries()) {
      const isActive = i === this.#currentSlide;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-pressed', String(isActive));
    }

    const prevBtn = this.content.querySelector('[data-qv-prev]');
    const nextBtn = this.content.querySelector('[data-qv-next]');

    if (prevBtn instanceof HTMLButtonElement) {
      prevBtn.disabled = this.#currentSlide === 0;
    }

    if (nextBtn instanceof HTMLButtonElement) {
      nextBtn.disabled = this.#currentSlide === slides.length - 1;
    }

    // Scroll thumb track so active thumb stays in the 3-visible window
    const track = this.content.querySelector('.qv-gallery__thumbs-track');
    if (track instanceof HTMLElement) {
      const THUMB_SLOT = 60; // 54px thumb + 6px gap
      const VISIBLE_HEIGHT = 174; // 3 thumbs × 54px + 2 gaps × 6px
      const activeTop = this.#currentSlide * THUMB_SLOT;
      const activeBottom = activeTop + 54;
      if (activeTop < track.scrollTop) {
        track.scrollTop = activeTop;
      } else if (activeBottom > track.scrollTop + VISIBLE_HEIGHT) {
        track.scrollTop = activeBottom - VISIBLE_HEIGHT;
      }
    }

    // Update mobile indicator bar position via CSS custom property
    const gallery = this.content.querySelector('.qv-gallery');
    if (gallery instanceof HTMLElement) {
      gallery.style.setProperty('--slide-index', String(this.#currentSlide));
    }
  }

  /**
   * Navigates gallery by a relative direction.
   *
   * @param {number} direction - +1 or -1
   */
  #navGallery(direction) {
    this.#showSlide(this.#currentSlide + direction);
  }

  // ── Quantity selector ─────────────────────────────────────────────────────

  /**
   * Adjusts the quantity input by a delta value.
   *
   * @param {number} delta
   */
  #adjustQty(delta) {
    if (!this.content) return;

    const input = this.content.querySelector('[data-qv-qty-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const current = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 99;

    input.value = String(Math.max(min, Math.min(max, current + delta)));
  }

  // ── Product form (AJAX add to cart) ──────────────────────────────────────

  /** Attaches AJAX submit handler to the injected product form. */
  #initForm() {
    const form = this.content?.querySelector('.qv-form');
    if (!(form instanceof HTMLFormElement)) return;

    form.addEventListener('submit', this.#onFormSubmit);
  }

  /** @param {SubmitEvent} event */
  #onFormSubmit = async (event) => {
    event.preventDefault();

    const form = /** @type {HTMLFormElement} */ (event.target);
    const submitBtn = form.querySelector('[type="submit"]');

    if (!(submitBtn instanceof HTMLButtonElement)) return;

    submitBtn.disabled = true;
    submitBtn.textContent = 'Adding…';

    try {
      // Collect cart section IDs so Shopify returns updated section HTML in the response,
      // which lets component-cart-items morph the mini cart without a second fetch.
      const sectionIds = /** @type {string[]} */ (
        [...document.querySelectorAll('cart-items-component')]
          .filter((el) => el instanceof HTMLElement && el.dataset.sectionId)
          .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      );

      const formData = new FormData(form);
      if (sectionIds.length) formData.set('sections', sectionIds.join(','));

      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!resp.ok) throw new Error(`Cart add failed: ${resp.status}`);

      const cartData = await resp.json();

      // Add any checked add-on products
      const checkedAddons = /** @type {NodeListOf<HTMLInputElement>} */ (
        this.content?.querySelectorAll('[data-qv-addon]:checked')
      );
      if (checkedAddons?.length) {
        await Promise.all(
          Array.from(checkedAddons).map((addon) =>
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: addon.dataset.qvAddon, quantity: 1 }),
            })
          )
        );
      }

      // Dispatch in the same shape as CartAddEvent (extends Event, sets .detail as a plain
      // property) so cart-icon, component-cart-items, and cart-drawer all handle it correctly.
      const itemCount = Number(formData.get('quantity')) || 1;
      document.dispatchEvent(
        Object.assign(new Event('cart:update', { bubbles: true }), {
          detail: {
            resource: cartData,
            sourceId: 'quick-view-dialog',
            data: {
              itemCount,
              sections: cartData.sections,
            },
          },
        })
      );

      submitBtn.textContent = 'Added!';

      // Re-fetch fresh section HTML (cache was cleared by #onCartUpdate above).
      // If the product switched to wishlist/requested mode, stay open and show
      // the request button. Otherwise close after the brief confirmation pause.
      const productUrl = this.#currentProductUrl;
      if (productUrl) {
        try {
          const freshHtml = await this.#fetchContent(productUrl);
          if (/data-mode="(wishlist|requested)"/.test(freshHtml) && this.content) {
            this.content.innerHTML = freshHtml;
            this.#currentSlide = 0;
            this.#initGallery();
            this.#initForm();
          } else {
            setTimeout(() => this.#close(), 800);
          }
        } catch {
          setTimeout(() => this.#close(), 800);
        }
      } else {
        setTimeout(() => this.#close(), 800);
      }
    } catch {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Add to Cart';
    }
  };
}

if (!customElements.get('quick-view-dialog')) {
  customElements.define('quick-view-dialog', QuickViewDialog);
}
